import {
  calculateSaleReturnRefundPaise,
  type CreateSaleReturnRequest,
  type ReturnableSaleResponse,
  type SaleReturnResponse,
} from "@medicorepay/shared";
import { useEffect, useMemo, useRef, useState } from "react";

export interface SimpleSaleReturnViewProps {
  getReturnableSale: (invoiceNumber: string) => Promise<ReturnableSaleResponse>;
  createSaleReturn: (
    body: CreateSaleReturnRequest,
    idempotencyKey: string,
  ) => Promise<SaleReturnResponse>;
  requestManagerApproval?: (
    body: CreateSaleReturnRequest,
    idempotencyKey: string,
  ) => Promise<{ id: string; status: string }>;
  getManagerApproval?: (
    approvalRequestId: string,
  ) => Promise<{ id: string; status: string }>;
  onBack: () => void;
  initialInvoiceNumber?: string;
  idempotencyScope?: string;
  printReceipt?: () => void;
}

interface PendingReturn {
  fingerprint: string;
  idempotencyKey: string;
}

type ReturnRefundMethod = CreateSaleReturnRequest["refunds"][number]["method"];

type ReturnStockCondition =
  | "SEALED_SALEABLE"
  | "OPENED"
  | "DAMAGED"
  | "EXPIRED"
  | "RECALLED"
  | "COLD_CHAIN_UNVERIFIED";

type ReturnStockDisposition =
  | "AVAILABLE"
  | "QUARANTINE"
  | "DAMAGED"
  | "EXPIRED"
  | "RECALLED";

const RETURN_STOCK_CONDITIONS: readonly {
  value: ReturnStockCondition;
  label: string;
}[] = [
  {
    value: "SEALED_SALEABLE",
    label: "Sealed & saleable — add to stock",
  },
  { value: "OPENED", label: "Opened — keep in quarantine" },
  { value: "DAMAGED", label: "Damaged — do not sell" },
  { value: "EXPIRED", label: "Expired — do not sell" },
  { value: "RECALLED", label: "Recalled — isolate" },
  {
    value: "COLD_CHAIN_UNVERIFIED",
    label: "Cold-chain uncertain — quarantine",
  },
];

function defaultConditionForReason(reason: string): ReturnStockCondition {
  if (reason === "DAMAGED") return "DAMAGED";
  if (reason === "QUALITY_ISSUE") return "OPENED";
  return "SEALED_SALEABLE";
}

function dispositionForCondition(
  condition: ReturnStockCondition,
): ReturnStockDisposition {
  return {
    SEALED_SALEABLE: "AVAILABLE",
    OPENED: "QUARANTINE",
    DAMAGED: "DAMAGED",
    EXPIRED: "EXPIRED",
    RECALLED: "RECALLED",
    COLD_CHAIN_UNVERIFIED: "QUARANTINE",
  }[condition] as ReturnStockDisposition;
}

const RETURN_REFUND_METHODS: readonly ReturnRefundMethod[] = [
  "CASH",
  "UPI",
  "CARD",
  "LOYALTY",
  "CREDIT",
];

function isReturnRefundMethod(value: string): value is ReturnRefundMethod {
  return RETURN_REFUND_METHODS.includes(value as ReturnRefundMethod);
}

function newCommandKey() {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `return-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

function moneyFromPaise(value: number) {
  return (value / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatQuantity(value: string | number) {
  const quantity = Number(value);
  if (!Number.isFinite(quantity)) return String(value);
  return quantity.toLocaleString("en-IN", {
    maximumFractionDigits: 6,
    useGrouping: false,
  });
}

function errorMessage(error: unknown) {
  const candidate = error as {
    response?: {
      message?: string;
      code?: string;
      remaining?: string;
      data?: { message?: string; code?: string; remaining?: string };
    };
    message?: string;
  };
  const data = candidate.response?.data ?? candidate.response;
  if (data?.code === "OVER_RETURN" && data.remaining) {
    return `Return exceeds the remaining quantity (${data.remaining}).`;
  }
  return data?.message || candidate.message || "Unable to process the return";
}

function errorCode(error: unknown) {
  const response = (
    error as {
      code?: string;
      response?: { code?: string; data?: { code?: string } };
    }
  ).response;
  return (
    response?.data?.code ?? response?.code ?? (error as { code?: string }).code
  );
}

export function SimpleSaleReturnView({
  getReturnableSale,
  createSaleReturn,
  requestManagerApproval,
  getManagerApproval,
  onBack,
  initialInvoiceNumber,
  idempotencyScope = "active-store",
  printReceipt = () => window.print(),
}: SimpleSaleReturnViewProps) {
  const initialInvoice = initialInvoiceNumber?.trim() ?? "";
  const [invoiceNumber, setInvoiceNumber] = useState(initialInvoice);
  const [searchEnabled, setSearchEnabled] = useState(!initialInvoice);
  const [sale, setSale] = useState<ReturnableSaleResponse | null>(null);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [conditions, setConditions] = useState<
    Record<string, ReturnStockCondition>
  >({});
  const [reason, setReason] = useState("CUSTOMER_RETURN");
  const [refundMethod, setRefundMethod] = useState<ReturnRefundMethod>("CASH");
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SaleReturnResponse | null>(null);
  const [approvalRequired, setApprovalRequired] = useState(false);
  const [requestingApproval, setRequestingApproval] = useState(false);
  const [checkingApproval, setCheckingApproval] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState<{
    tone: "info" | "success" | "error";
    text: string;
  } | null>(null);
  const [managerApprovalRequestId, setManagerApprovalRequestId] = useState("");
  const pending = useRef<PendingReturn | null>(null);
  const autoLoadedInvoice = useRef<string | null>(null);
  const storageKey = `medicorepay:sale-return:${idempotencyScope}:pending`;

  const selected = useMemo(() => {
    if (!sale) return [];
    const returnedPaidByLine = new Map<string, number>();
    for (const lot of sale.lots) {
      returnedPaidByLine.set(
        lot.originalSaleLineId,
        (returnedPaidByLine.get(lot.originalSaleLineId) ?? 0) +
          Number(lot.returnedPaidQuantity ?? 0),
      );
    }
    const preparedPaidByLine = new Map<string, number>();
    return sale.lots.flatMap((lot) => {
      const quantity = Number(quantities[lot.lotId] || 0);
      if (!Number.isFinite(quantity) || quantity <= 0) return [];
      const returnablePaidQuantity = Number(
        lot.returnablePaidQuantity ?? lot.originalLineQuantity,
      );
      const refundablePaidQuantity = Math.min(
        quantity,
        Math.max(0, returnablePaidQuantity),
      );
      const previouslyReturnedPaidQuantity =
        (returnedPaidByLine.get(lot.originalSaleLineId) ?? 0) +
        (preparedPaidByLine.get(lot.originalSaleLineId) ?? 0);
      preparedPaidByLine.set(
        lot.originalSaleLineId,
        (preparedPaidByLine.get(lot.originalSaleLineId) ?? 0) +
          refundablePaidQuantity,
      );
      return [
        {
          lot,
          quantity,
          condition: conditions[lot.lotId] ?? defaultConditionForReason(reason),
          refundPaise: calculateSaleReturnRefundPaise(
            lot.lineTotalPaise,
            Number(lot.originalLineQuantity),
            refundablePaidQuantity,
            lot.settlementAdjustmentPaise ?? 0,
            previouslyReturnedPaidQuantity,
          ),
        },
      ];
    });
  }, [conditions, quantities, reason, sale]);
  const refundPaise = selected.reduce((sum, line) => sum + line.refundPaise, 0);

  const loadBill = async (invoice: string) => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const loaded = await getReturnableSale(invoice);
      setSale(loaded);
      setInvoiceNumber(loaded.invoiceNumber);
      setQuantities({});
      setConditions(
        Object.fromEntries(
          loaded.lots.map((lot) => [
            lot.lotId,
            defaultConditionForReason(reason),
          ]),
        ),
      );
      setApprovalRequired(Boolean(loaded.eligibility.managerOverrideRequired));
      setApprovalMessage(null);
      setManagerApprovalRequestId("");
      const originalMethod = loaded.originalTenders[0]?.method;
      if (originalMethod && isReturnRefundMethod(originalMethod)) {
        setRefundMethod(originalMethod);
      }
    } catch (loadError) {
      setSale(null);
      setError(errorMessage(loadError));
      setSearchEnabled(true);
    } finally {
      setLoading(false);
    }
  };

  const findBill = async (event: React.FormEvent) => {
    event.preventDefault();
    const invoice = invoiceNumber.trim();
    if (!invoice) return setError("Enter a bill number");
    await loadBill(invoice);
  };

  useEffect(() => {
    const invoice = initialInvoiceNumber?.trim();
    if (!invoice || autoLoadedInvoice.current === invoice) return;
    autoLoadedInvoice.current = invoice;
    setInvoiceNumber(invoice);
    setSearchEnabled(false);
    void loadBill(invoice);
  }, [initialInvoiceNumber]);

  const pendingCommand = (fingerprint: string) => {
    if (!pending.current) {
      try {
        const stored = sessionStorage.getItem(storageKey);
        pending.current = stored ? (JSON.parse(stored) as PendingReturn) : null;
      } catch {
        pending.current = null;
      }
    }
    if (pending.current?.fingerprint === fingerprint) return pending.current;
    pending.current = { fingerprint, idempotencyKey: newCommandKey() };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(pending.current));
    } catch {
      // The in-memory key still protects retries in this mounted flow.
    }
    return pending.current;
  };

  const buildReturnRequest = (): CreateSaleReturnRequest | null => {
    if (!sale || selected.length === 0) return null;
    return {
      originalSaleId: sale.saleId,
      reason,
      lines: selected.map(({ lot, quantity, condition }) => ({
        originalSaleLineId: lot.originalSaleLineId,
        lotId: lot.lotId,
        quantity: quantity.toFixed(3),
        reason,
        condition,
        disposition: dispositionForCondition(condition),
      })),
      refunds:
        refundPaise > 0
          ? [
              {
                method: refundMethod,
                amountPaise: refundPaise,
                ...(reference.trim() ? { reference: reference.trim() } : {}),
              },
            ]
          : [],
      ...(managerApprovalRequestId.trim()
        ? { managerApprovalRequestId: managerApprovalRequestId.trim() }
        : {}),
    };
  };

  const submit = async () => {
    if (!sale) return;
    setError(null);
    if (sale.eligibility.requiresCanonicalization) {
      return setError(
        "This historical bill must be canonicalized before a commercial return can be posted.",
      );
    }
    if (selected.length === 0) return setError("Enter a return quantity");
    for (const line of selected) {
      if (line.quantity > Number(line.lot.returnableQuantity)) {
        return setError(
          `${line.lot.productName} exceeds the returnable quantity of ${line.lot.returnableQuantity}`,
        );
      }
    }
    if (!reason.trim()) return setError("Select a return reason");
    const body = buildReturnRequest()!;
    const fingerprint = JSON.stringify(body);
    const command = pendingCommand(fingerprint);
    setSubmitting(true);
    try {
      const completed = await createSaleReturn(body, command.idempotencyKey);
      pending.current = null;
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // The accepted server response is authoritative.
      }
      setResult(completed);
    } catch (submitError) {
      if (errorCode(submitError) === "SALE_RETURN_MANAGER_APPROVAL_REQUIRED") {
        setApprovalRequired(true);
        setError(
          "A manager must approve this return. Request approval below, then retry after it is approved.",
        );
      } else {
        setError(
          `${errorMessage(submitError)} Retry uses the same safe command.`,
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const requestApproval = async () => {
    const body = buildReturnRequest();
    if (!body || !requestManagerApproval) return;
    const { managerApprovalRequestId: _existing, ...approvalBody } = body;
    setRequestingApproval(true);
    setError(null);
    setApprovalMessage(null);
    try {
      const approval = await requestManagerApproval(
        approvalBody,
        newCommandKey(),
      );
      setManagerApprovalRequestId(approval.id);
      setApprovalMessage({
        tone: "info",
        text: `Approval request ${approval.id} is ${approval.status.toLowerCase()}. Give this ID to an authorized manager using Approval Center, then check its status here.`,
      });
    } catch (approvalError) {
      setError(errorMessage(approvalError));
    } finally {
      setRequestingApproval(false);
    }
  };

  const checkApproval = async () => {
    const approvalRequestId = managerApprovalRequestId.trim();
    if (!approvalRequestId || !getManagerApproval) return;
    setCheckingApproval(true);
    setError(null);
    setApprovalMessage(null);
    try {
      const approval = await getManagerApproval(approvalRequestId);
      if (approval.status === "APPROVED") {
        setApprovalMessage({
          tone: "success",
          text: "Manager approval granted. Select Accept return to complete the return.",
        });
      } else if (approval.status === "REJECTED") {
        setApprovalMessage({
          tone: "error",
          text: "This manager approval was rejected. Review the return details or request a new approval.",
        });
      } else if (approval.status === "CONSUMED") {
        setApprovalMessage({
          tone: "error",
          text: "This approval was already used. Reload the original bill before attempting another return.",
        });
      } else {
        setApprovalMessage({
          tone: "info",
          text: `Manager approval is ${approval.status.toLowerCase()}. Ask an authorized manager to review it in Approval Center.`,
        });
      }
    } catch (approvalError) {
      setError(errorMessage(approvalError));
    } finally {
      setCheckingApproval(false);
    }
  };

  if (result && sale) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <div className="print-receipt rounded-xl border border-green-200 bg-white p-8 shadow-sm">
          <div className="text-sm font-semibold uppercase tracking-wide text-green-700">
            {result.replayed
              ? "Already returned — safe replay"
              : "Return accepted"}
          </div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            {result.returnNumber}
          </h1>
          <div className="mt-6 space-y-2 border-y border-dashed py-4 text-sm">
            <div className="flex justify-between">
              <span>Original bill</span>
              <strong>{sale.invoiceNumber}</strong>
            </div>
            <div className="flex justify-between">
              <span>Accepted</span>
              <span>{new Date(result.acceptedAt).toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between">
              <span>Refund method</span>
              <span>{refundMethod}</span>
            </div>
            <div className="flex justify-between text-lg">
              <span>Refund</span>
              <strong>₹{moneyFromPaise(result.refundPaise)}</strong>
            </div>
          </div>
          <table className="mt-4 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="py-2">Product / batch</th>
                <th>Quantity</th>
                <th className="text-right">Refund</th>
              </tr>
            </thead>
            <tbody>
              {selected.map((line) => (
                <tr key={line.lot.lotId} className="border-t">
                  <td className="py-2">
                    <strong>{line.lot.productName}</strong>
                    <div className="text-xs text-gray-500">
                      {line.lot.batchNumber}
                    </div>
                  </td>
                  <td>{formatQuantity(line.quantity)}</td>
                  <td className="text-right">
                    ₹{moneyFromPaise(line.refundPaise)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-6 flex flex-wrap gap-3 print:hidden">
            <button
              type="button"
              onClick={printReceipt}
              className="rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white hover:bg-blue-700"
            >
              Print return
            </button>
            <button
              type="button"
              onClick={() => {
                setSale(null);
                setResult(null);
                setInvoiceNumber("");
                setConditions({});
                setSearchEnabled(true);
              }}
              className="rounded-lg border border-gray-300 px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50"
            >
              Return another bill
            </button>
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-gray-300 px-5 py-2.5 font-medium text-gray-700 hover:bg-gray-50"
            >
              Return history
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <header>
        <button
          type="button"
          onClick={onBack}
          className="mb-2 text-sm font-medium text-blue-700 hover:text-blue-900"
        >
          ← Back to returns
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          Return against a bill
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {searchEnabled
            ? "Find the original bill, then return only the exact product lot and quantity."
            : "The original bill is loaded from the invoice. Select the product and quantity to return."}
        </p>
      </header>
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {error}
        </div>
      )}
      {searchEnabled ? (
        <form
          onSubmit={findBill}
          className="flex max-w-2xl gap-3 rounded-xl bg-white p-5 shadow-sm"
        >
          <label className="flex-1 text-sm font-medium text-gray-700">
            Bill number
            <input
              value={invoiceNumber}
              onChange={(event) => setInvoiceNumber(event.target.value)}
              placeholder="Invoice number"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 font-normal"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="self-end rounded-lg bg-blue-600 px-5 py-2.5 font-medium text-white disabled:opacity-50"
          >
            {loading ? "Finding…" : "Find bill"}
          </button>
        </form>
      ) : (
        loading && (
          <div
            role="status"
            className="max-w-2xl rounded-xl bg-white p-5 text-sm text-gray-600 shadow-sm"
          >
            Loading original bill…
          </div>
        )
      )}

      {sale && (
        <>
          {sale.eligibility.requiresCanonicalization && (
            <div
              role="status"
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              Historical bill found. Its original quantities are visible, but it
              must be canonicalized before stock, GST credit note or refund can
              be posted.
            </div>
          )}
          <section className="rounded-xl bg-white p-5 shadow-sm">
            <div className="flex flex-wrap justify-between gap-3 border-b pb-4">
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Original bill
                </div>
                <div className="text-xl font-bold">{sale.invoiceNumber}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">Customer</div>
                <div className="font-medium">
                  {sale.customer?.name || "Walk-in customer"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Bill total
                </div>
                <div className="font-medium">
                  ₹{moneyFromPaise(sale.payablePaise)}
                </div>
              </div>
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[980px] text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-3 py-3">Product</th>
                    <th>Batch / expiry</th>
                    <th>Sold</th>
                    <th>Already returned</th>
                    <th>Returnable</th>
                    <th>Stock condition</th>
                    <th>Return now</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sale.lots.map((lot) => (
                    <tr key={lot.lotId}>
                      <td className="px-3 py-3">
                        <strong>{lot.productName}</strong>
                        <div className="text-xs text-gray-500">{lot.sku}</div>
                      </td>
                      <td>
                        {lot.batchNumber}
                        <div className="text-xs text-gray-500">
                          {lot.expiryDate
                            ? new Date(lot.expiryDate).toLocaleDateString(
                                "en-IN",
                              )
                            : "No expiry"}
                        </div>
                      </td>
                      <td>{formatQuantity(lot.soldQuantity)}</td>
                      <td>{formatQuantity(lot.returnedQuantity)}</td>
                      <td>{formatQuantity(lot.returnableQuantity)}</td>
                      <td className="pr-3">
                        <select
                          aria-label={`${lot.productName} return condition`}
                          disabled={
                            sale.eligibility.requiresCanonicalization ||
                            Number(lot.returnableQuantity) <= 0
                          }
                          value={
                            conditions[lot.lotId] ??
                            defaultConditionForReason(reason)
                          }
                          onChange={(event) =>
                            setConditions((current) => ({
                              ...current,
                              [lot.lotId]: event.target
                                .value as ReturnStockCondition,
                            }))
                          }
                          className="w-64 rounded border border-gray-300 px-2 py-1.5"
                        >
                          {RETURN_STOCK_CONDITIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          aria-label={`${lot.productName} return quantity`}
                          type="number"
                          min="0"
                          max={Number(lot.returnableQuantity)}
                          step="0.001"
                          disabled={
                            sale.eligibility.requiresCanonicalization ||
                            Number(lot.returnableQuantity) <= 0
                          }
                          value={quantities[lot.lotId] ?? ""}
                          onChange={(event) =>
                            setQuantities((current) => ({
                              ...current,
                              [lot.lotId]: event.target.value,
                            }))
                          }
                          className="w-28 rounded border border-gray-300 px-2 py-1.5 text-right"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          <section className="grid gap-4 rounded-xl bg-white p-5 shadow-sm md:grid-cols-3">
            {approvalRequired && (
              <div className="md:col-span-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
                <div className="font-semibold text-amber-900">
                  Manager approval required
                </div>
                <p className="mt-1 text-sm text-amber-800">
                  Request approval, then have a different authorized manager
                  review it in{" "}
                  <a
                    className="font-semibold underline"
                    href={
                      managerApprovalRequestId.trim()
                        ? `/approvals?requestId=${encodeURIComponent(managerApprovalRequestId.trim())}`
                        : "/approvals"
                    }
                  >
                    Approval Center
                  </a>
                  .
                </p>
                <div className="mt-3 flex flex-wrap gap-3">
                  <input
                    aria-label="Manager approval request ID"
                    value={managerApprovalRequestId}
                    onChange={(event) =>
                      setManagerApprovalRequestId(event.target.value)
                    }
                    placeholder="Approved request UUID"
                    className="min-w-72 flex-1 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm"
                  />
                  {requestManagerApproval && (
                    <button
                      type="button"
                      onClick={() => void requestApproval()}
                      disabled={requestingApproval}
                      className="rounded-lg bg-amber-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      {requestingApproval
                        ? "Requesting…"
                        : "Request manager approval"}
                    </button>
                  )}
                  {getManagerApproval && managerApprovalRequestId.trim() && (
                    <button
                      type="button"
                      onClick={() => void checkApproval()}
                      disabled={checkingApproval}
                      className="rounded-lg border border-amber-700 bg-white px-4 py-2 text-sm font-semibold text-amber-900 disabled:opacity-50"
                    >
                      {checkingApproval ? "Checking…" : "Check approval status"}
                    </button>
                  )}
                </div>
                {approvalMessage && (
                  <div
                    role="status"
                    className={`mt-3 rounded-lg border px-3 py-2 text-sm ${
                      approvalMessage.tone === "success"
                        ? "border-green-200 bg-green-50 text-green-800"
                        : approvalMessage.tone === "error"
                          ? "border-red-200 bg-red-50 text-red-800"
                          : "border-blue-200 bg-blue-50 text-blue-800"
                    }`}
                  >
                    {approvalMessage.text}
                  </div>
                )}
              </div>
            )}
            <label className="text-sm font-medium text-gray-700">
              Reason
              <select
                disabled={sale.eligibility.requiresCanonicalization}
                value={reason}
                onChange={(event) => {
                  const nextReason = event.target.value;
                  setReason(nextReason);
                  const nextCondition = defaultConditionForReason(nextReason);
                  setConditions(
                    Object.fromEntries(
                      sale.lots.map((lot) => [lot.lotId, nextCondition]),
                    ),
                  );
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
              >
                <option value="CUSTOMER_RETURN">Customer return</option>
                <option value="WRONG_PRODUCT">Wrong product</option>
                <option value="DAMAGED">Damaged product</option>
                <option value="QUALITY_ISSUE">Quality issue</option>
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Refund method
              <select
                disabled={sale.eligibility.requiresCanonicalization}
                value={refundMethod}
                onChange={(event) =>
                  setRefundMethod(event.target.value as typeof refundMethod)
                }
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
              >
                <option value="CASH">Cash</option>
                <option value="UPI">UPI</option>
                <option value="CARD">Card</option>
                <option value="CREDIT">Store credit</option>
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Reference (optional)
              <input
                disabled={sale.eligibility.requiresCanonicalization}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal"
              />
            </label>
            <div className="md:col-span-3 flex flex-wrap items-center justify-between gap-4 border-t pt-4">
              <div>
                <div className="text-xs uppercase text-gray-500">
                  Refund total
                </div>
                <div className="text-2xl font-bold">
                  ₹{moneyFromPaise(refundPaise)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={
                  sale.eligibility.requiresCanonicalization ||
                  submitting ||
                  selected.length === 0
                }
                className="rounded-lg bg-green-600 px-6 py-3 font-semibold text-white hover:bg-green-700 disabled:opacity-50"
              >
                {submitting ? "Returning safely…" : "Accept return"}
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
